import { BehaviorSubject, Observable, of } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { UserService } from './user.service';
import { Field, FieldActivity, User } from '../_models';
import { AppState } from '../_reducers';
import { environment } from '../../../environments/environment';
import { selectAllFieldActivities } from '../_selectors/fieldActivity.selectors';
import { XYearService } from './xYear.service';
import { TagService } from './tag.service';

/**
 * @deprecated Use `FieldActivityService` instead.
 */
@Injectable()
export class FieldActivityDeprecatedService {
  private activityServiceUrl = environment.activityServiceUrl + 'api/private/v2/cache/';
  fieldActivityList: Observable<FieldActivity[]>;
  resultList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<Array<FieldActivity>>(
    []
  );
  originalFieldActivityList$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);
  currentUser: User = new User();
  allFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >(null);
  lastFieldActivity$: BehaviorSubject<Array<FieldActivity>> = new BehaviorSubject<
    Array<FieldActivity>
  >([]);

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private userService: UserService,
    private xYearService: XYearService,
    private tagService: TagService
  ) {
    this.fieldActivityList = this.store.pipe(select(selectAllFieldActivities));
    this.fieldActivityList.subscribe((res) => {
      this.originalFieldActivityList$.next(res);
    });

    this.userService.getUserMeFromLocalStore().subscribe((u: User) => {
      this.currentUser = u;
    });
  }

  getFromLocalStore(): Observable<FieldActivity[]> {
    return this.fieldActivityList;
  }

  // Used for rxjs state by the effect 'loadFieldActivities'
  getFieldActivities(selectedYear: number): Observable<FieldActivity[]> {
    const year = selectedYear ?? this.xYearService.getYear();
    const tagQuery = this.tagService.getTagQuery();

    // User settings handling
    const { activityPersonalInfo, activityPersonalInfoEmployee } =
      this.currentUser?.userSettings ?? {};
    const onlyPersonalActivity = activityPersonalInfoEmployee ?? activityPersonalInfo ?? false;

    // Excludes query building
    const excludesList = ['machineActivity', 'machineActivities', 'equipmentActivities'];
    const excludesAppend = excludesList.map((item) => `&excludes=${item}`).join('');

    return this.http
      .get<
        FieldActivity[]
      >(this.activityServiceUrl + 'fieldActivities?onlyPersonalActivity=' + onlyPersonalActivity + excludesAppend + '&cropYear=' + year + tagQuery)
      .pipe(
        tap((response) => {
          this.allFieldActivity$.next(response);
        })
      );
  }

  /**
   * ⚠️ Methods under evaluation
   */

  // Replace with call to get specific FA
  getLocalFieldActivityById = (id): FieldActivity => {
    const list = this.originalFieldActivityList$.value;
    const item = list?.find((i) => i.id === id);

    return item ? structuredClone(item) : null;
  };

  // Equipment and incab
  setFilteredFaByIdByObservablelist(faIds: number[]): void {
    const result = faIds
      .map((id) => this.getLocalFieldActivityById(id))
      .filter((fa): fa is FieldActivity => !!fa);

    this.resultList$.next(result);
  }

  getFilteredFaByIdByObservablelist() {
    return this.resultList$.asObservable();
  }

  /**
   * 1. This method is used in sustainability v2 -> regenerative with fake pagination (getting all fa) - Needed to show data about relevant activity tables
   * 2. This method is used in containers details to show in table cropCommodityName e fieldName (they should return from backend)
   */
  getFieldActivitiesWithPagination(
    selectedYear: number,
    all: boolean = null
  ): Observable<FieldActivity[]> {
    let onlyPersonalActivity = 'false';
    if (this.currentUser && this.currentUser.userSettings) {
      if (this.currentUser.userSettings.activityPersonalInfo !== undefined) {
        onlyPersonalActivity = this.currentUser.userSettings.activityPersonalInfo.toString();
      }
    }

    let params = new HttpParams()
      .set('onlyPersonalActivity', onlyPersonalActivity)
      .set('cropYear', selectedYear?.toString() || '')
      .append('excludes', 'machineActivity')
      .append('excludes', 'machineActivities')
      .append('excludes', 'equipmentActivities');

    if (all) {
      params = params.set('all', all.toString());
    }

    return this.http
      .get<FieldActivity[]>(this.activityServiceUrl + 'fieldActivities', { params })
      .pipe(
        map((response) => {
          this.lastFieldActivity$.next(response);
          return response;
        })
      );
  }

  /**
   * This method is used in activity detail to understand if there are more than 1 FA with the same uuid
   * Needed to show popup to address FA as single or grouped
   */
  async getLocalFieldActivityByUUID(uuid: string): Promise<Array<FieldActivity>> {
    if (!uuid) {
      return [];
    }

    const fas = await firstValueFrom(
      this.fieldActivityList.pipe(first((list: FieldActivity[]) => list && list.length > 0))
    );

    return fas.filter((f) => f.uuid === uuid);
  }

  /**
   * This method is used in map control center. To get all last activity by field
   * Needed to color polygon into the map
   */
  async retrieveLastDoneActivityFromField(field: Field): Promise<FieldActivity | null> {
    const activities = await this.getLocalFieldActivityDone(field.id);
    if (!activities?.length) {
      return null;
    }

    let latest = activities[0];
    let latestTime = new Date(latest.activityDate).getTime();

    for (let i = 1; i < activities.length; i++) {
      const currentTime = new Date(activities[i].activityDate).getTime();
      if (currentTime > latestTime) {
        latest = activities[i];
        latestTime = currentTime;
      }
    }

    return latest || null;
  }

  private async getLocalFieldActivityDone(id: number): Promise<FieldActivity[]> {
    if (!id) {
      return [];
    }

    const fas = await firstValueFrom(this.fieldActivityList);

    return fas.filter((fa) => fa?.field?.id === id && fa.done !== false);
  }

  /**
   * This method is used as a guard when attempting to delete a crop.
   * It performs a preliminary check to determine whether the deletion
   * should be allowed or blocked.
   */
  checkIfCropExistInFieldActivity(idCrop: number): Observable<FieldActivity[]> {
    if (!idCrop) {
      return of([]);
    }

    return this.fieldActivityList.pipe(map((list) => list.filter((f) => f.crop?.id === idCrop)));
  }

  /**
   * This method is used as a guard when attempting to delete a product.
   * It performs a preliminary check to determine whether the deletion
   * should be allowed or blocked.
   */
  checkIfProductIdExistInFieldActivity(productId: number): Array<FieldActivity> {
    const result: FieldActivity[] = [];

    this.fieldActivityList
      .pipe(first((list: FieldActivity[]) => !!list?.length))
      .subscribe((activities: FieldActivity[]) => {
        activities.forEach((activity) => {
          if (!activity?.mixes?.length) {
            return;
          }

          const hasProduct = activity.mixes.some((mix) => mix?.product?.id === productId);

          if (hasProduct && !result.includes(activity)) {
            result.push(activity);
          }
        });
      });

    return result;
  }
}
